// ──────────────────────────────────────────────────────────────────────────
// Triggers slice — gates are trigger-category asset instances.
//
// Entities are sourced from those instances (loadSceneTriggers); reducers hold
// the persisted shape plus session-only state (pending/waiting/fired + firedAt).
// `updateTriggerState` is the firing hot-path: fire sites must dispatch
// state='fired' WITH firedAt synchronously.
// ──────────────────────────────────────────────────────────────────────────

import { createSlice, createEntityAdapter, type PayloadAction, createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/store/rootState';
import type { TriggerInstance } from '@/types/triggerTypes';

// Entity adapter for triggers (uses 'instanceId' as the ID field)
const triggersAdapter = createEntityAdapter<TriggerInstance, string>({
  selectId: (trigger) => trigger.instanceId,
  sortComparer: (a, b) => a.time - b.time,
});

export interface TriggersState extends ReturnType<typeof triggersAdapter.getInitialState> {
  loadingByAssetId: Record<string, boolean>;
  idsByAssetId: Record<string, string[]>;
  /** Session-only: gates fire themselves on reaching `waiting` during playback. */
  autoplay: boolean;
  autoplayWaitFrom: Record<string, number>;
  /** Where the playhead stood when autoplay came on mid-play, or null; see `autoplayFires`. */
  autoplayFrom: number | null;
}

const initialState: TriggersState = {
  ...triggersAdapter.getInitialState(),
  loadingByAssetId: {},
  idsByAssetId: {},
  autoplay: true,
  autoplayWaitFrom: {},
  autoplayFrom: null,
};

const slice = createSlice({
  name: 'triggers',
  initialState,
  reducers: {
    setAssetTriggers: (
      state,
      action: PayloadAction<{ assetInstanceId: string; triggers: TriggerInstance[] }>
    ) => {
      const { assetInstanceId, triggers } = action.payload;
      const nextIds = triggers.map((trigger) => trigger.instanceId);
      // upsertMany never removes; drop entities no longer present or they linger.
      const removed = (state.idsByAssetId[assetInstanceId] || []).filter((id) => !nextIds.includes(id));
      if (removed.length) triggersAdapter.removeMany(state, removed);
      triggersAdapter.upsertMany(state, triggers);
      state.idsByAssetId[assetInstanceId] = nextIds;
      state.loadingByAssetId[assetInstanceId] = false;
    },
    upsertTrigger: (state, action: PayloadAction<TriggerInstance>) => {
      triggersAdapter.upsertOne(state, action.payload);
      const assetId = action.payload.assetInstanceId;
      const triggerId = action.payload.instanceId;
      
      // Update asset mapping
      const assetList = state.idsByAssetId[assetId] || [];
      if (!assetList.includes(triggerId)) {
        state.idsByAssetId[assetId] = [...assetList, triggerId];
      }
    },
    updateTriggerState: (state, action: PayloadAction<{ triggerInstanceId: string; state: TriggerInstance['state']; firedAt?: number }>) => {
      const { triggerInstanceId, state: newState, firedAt } = action.payload;
      const trigger = state.entities[triggerInstanceId];
      if (trigger) {
        trigger.state = newState;
        if (newState === 'fired') {
          trigger.firedAt = firedAt ?? trigger.firedAt ?? null;
        } else {
          // pending / waiting / expired → reset firedAt so the next fire records fresh
          trigger.firedAt = null;
        }
      }
    },
    removeTrigger: (state, action: PayloadAction<{ instanceId: string; assetId: string }>) => {
      const { instanceId, assetId } = action.payload;
      triggersAdapter.removeOne(state, instanceId);
      
      // Remove from asset mapping
      const assetList = state.idsByAssetId[assetId] || [];
      state.idsByAssetId[assetId] = assetList.filter((id) => id !== instanceId);
    },
    setAssetTriggersLoading: (state, action: PayloadAction<{ assetInstanceId: string; loading: boolean }>) => {
      state.loadingByAssetId[action.payload.assetInstanceId] = action.payload.loading;
    },
    clearTriggersForAsset: (state, action: PayloadAction<string>) => {
      const assetId = action.payload;
      const ids = state.idsByAssetId[assetId] || [];
      triggersAdapter.removeMany(state, ids);
      delete state.idsByAssetId[assetId];
      delete state.loadingByAssetId[assetId];
    },
    clearAllTriggers: (state) => {
      triggersAdapter.removeAll(state);
      state.idsByAssetId = {};
      state.loadingByAssetId = {};
    },
    // Survives scene switches and Reset: it is how the creator wants to watch, not scene data.
    setTriggerAutoplay: (state, action: PayloadAction<boolean>) => {
      state.autoplay = action.payload;
    },
    setAutoplayWait: (state, action: PayloadAction<{ triggerInstanceId: string; from: number }>) => {
      state.autoplayWaitFrom[action.payload.triggerInstanceId] = action.payload.from;
    },
    setAutoplayFrom: (state, action: PayloadAction<number | null>) => {
      state.autoplayFrom = action.payload;
    },
    clearAutoplayWait: (state, action: PayloadAction<string | null>) => {
      if (action.payload == null) {
        if (Object.keys(state.autoplayWaitFrom).length > 0) state.autoplayWaitFrom = {};
      } else if (action.payload in state.autoplayWaitFrom) {
        delete state.autoplayWaitFrom[action.payload];
      }
    },
  },
});

export const {
  setAutoplayWait,
  clearAutoplayWait,
  setAutoplayFrom,
  setAssetTriggers,
  upsertTrigger,
  updateTriggerState,
  removeTrigger,
  setAssetTriggersLoading,
  clearTriggersForAsset,
  clearAllTriggers,
  setTriggerAutoplay,
} = slice.actions;

export default slice.reducer;

// Selectors
const selectors = triggersAdapter.getSelectors<RootState>((state) => state.triggers);

export const selectTriggerById = (state: RootState, id: string) => selectors.selectById(state, id);

// Memoized selector to prevent unnecessary re-renders
export const selectTriggersByAssetId = createSelector(
  [
    (state: RootState) => state.triggers.idsByAssetId,
    (state: RootState) => state.triggers.entities,
    (state: RootState, assetInstanceId: string) => assetInstanceId
  ],
  (idsByAssetId, entities, assetInstanceId): TriggerInstance[] => {
    const ids = idsByAssetId[assetInstanceId] || [];
    return ids
      .map((id) => entities[id])
      .filter((trigger): trigger is TriggerInstance => Boolean(trigger))
      .sort((a, b) => a.time - b.time);
  }
);

export const selectTriggerAutoplay = (state: RootState): boolean => state.triggers.autoplay;
export const selectAutoplayWaitFrom = (state: RootState): Record<string, number> => state.triggers.autoplayWaitFrom;
export const selectAutoplayFrom = (state: RootState): number | null => state.triggers.autoplayFrom;

export const selectTriggersLoadingByAssetId = (state: RootState, assetInstanceId: string) =>
  state.triggers.loadingByAssetId[assetInstanceId] || false;

// Selector to get all triggers across all assets (for timescale rendering), memoised so it returns the same array until the triggers change
export const selectAllTriggers = createSelector(
  [(state: RootState) => state.triggers.entities],
  (entities): TriggerInstance[] => Object.values(entities).filter((trigger): trigger is TriggerInstance => Boolean(trigger))
);
